import React, { useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { openInCloudViewer } from 'utils'
import { getFileMetadata, isPreviewableImageFile, safeFileUrl } from 'components/UploadedFileList/fileMetadata'
import useLocaleText from 'components/useLocaleText'
import { FileThumbnail } from '../FileThumbnail'
import { MediaUploadPreview } from '../MediaUploadPreview'
import { Button } from '../Button'
import { mediaGalleryText } from './literals'
import styles from './styles.module.css'

interface IFileTileProps {
  url: string
  uploading?: boolean
  failed?: boolean
  onRemove?: (url: string) => void
  onRetry?: (url: string) => void
  onPreview?: () => void
  size?: number
}

/** The same file tile can live in a plain gallery or a shared media grid. */
export function FileTile({
  url,
  uploading = false,
  failed = false,
  onRemove,
  onRetry,
  onPreview,
  size = 100,
}: IFileTileProps) {
  const t = useLocaleText(mediaGalleryText)
  const [failedImageUrl, setFailedImageUrl] = useState<string>()
  const metadata = getFileMetadata(url)
  const name = metadata.name || t.file
  const href = safeFileUrl(url)
  const image = !!href && isPreviewableImageFile(metadata) && failedImageUrl !== url

  return (
    <MediaUploadPreview
      mediaType={image ? 'image' : 'file'}
      src={image ? href : undefined}
      onMediaError={() => setFailedImageUrl(url)}
      previewContent={<FileThumbnail name={name} mime={metadata.mime} />}
      alt={name}
      title={name}
      href={href ? openInCloudViewer(href, name) : undefined}
      onPreview={image ? onPreview : undefined}
      previewDisabled={uploading || failed}
      previewLabel={t.preview.replace('{name}', name)}
      uploadingLabel={t.uploading.replace('{name}', name)}
      uploading={uploading}
      error={failed}
      errorLabel={t.failed}
      style={{ width: size, height: size }}
      className={styles.thumbnail}
      removeAction={onRemove ? { label: t.remove.replace('{name}', name), onClick: () => onRemove(url) } : undefined}
      overlay={
        failed && onRetry ? (
          <Button
            type="button"
            variant="icon"
            className={styles.retry}
            aria-label={t.retry.replace('{name}', name)}
            onClick={() => onRetry(url)}
          >
            <RotateCcw size={14} />
          </Button>
        ) : null
      }
    />
  )
}
